import { useCallback, useEffect, useState } from 'react';
import { TaskTable } from '../../../TaskTable';
import { RejectionModal } from '@components/molecules/RejectionModal';
import { TaskDetailModal } from '@components/organisms/TaskDetailModal';
import { TaskStatus, Task } from '@models/task.model';
import TasksGraphQL from '@graphql/task.queries';
import { graphQlClient } from '@config/graphqlClient';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { SnackType } from '@models/common.model';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { useParams } from 'react-router-dom';
import { parseTaskDate } from '@utils/dateOnlyParser';
import ProjectsGraphQL from '@graphql/project.queries';
import { getProjectByIdRequest } from '@models/project.model';
import { useTranslation } from '@hooks/useTranslation';
import { RootState } from '@redux/store';
import { UserRoles } from '@models/user.model';

interface RouteParams {
  id: string;
  status: string;
}

interface CustomizedTableProps {
  tasks: any;
  handleTaskDetail: (id: string) => void;
  handleTaskEdit: (id: string) => void;
  handleEventDetail: (id: string) => void;
  handleEventEdit: (id: string) => void;
  reloadTasks: () => void;
  taskBudgetRequests?: Map<string, any[]>;
  onDateClick?: (dateValue: string | Date) => void;
}

export default function CustomizedTables({
  tasks,
  handleTaskDetail,
  handleTaskEdit,
  handleEventDetail,
  handleEventEdit,
  reloadTasks,
  taskBudgetRequests,
  onDateClick,
}: CustomizedTableProps) {
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isTaskDetailModalOpen, setIsTaskDetailModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const { role, userId } = useAppSelector((state: RootState) => state.user);
  const { id } = useParams<RouteParams>();
  const localeCommon = useTranslation('common');
  const dispatch = useAppDispatch();

  const formatDate = (dateString: string | Date) => {
    if (dateString instanceof Date) {
      return dateString.toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }
    const date = parseTaskDate(dateString);
    if (!date) return dateString;

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };
    return date.toLocaleString('en-US', options);
  };

  const getProjectById = useCallback(async (projectId: string) => {
    try {
      const variables = { projectId };
      const data: getProjectByIdRequest = await graphQlClient.request(
        ProjectsGraphQL.queries.getProjectById,
        variables,
      );
      if (data.getProjectById) {
        setProject(data.getProjectById);
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message:
            e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  }, [dispatch, localeCommon]);

  useEffect(() => {
    if (id) {
      getProjectById(id);
    }
  }, [id, getProjectById]);

  const canApproveTask = (task: any): boolean => {
    // Waiting on THIS organization: its own crew's work in internal approval, or delegated work it
    // is the client for. A copy waiting on its own client is not this organization's decision. The
    // server is the authority (it also knows whether a middle company already forwarded); this only
    // decides whether to show the buttons.
    const awaitingThisOrg =
      (task.status === TaskStatus.INTERNAL_APPROVAL && !task.delegateTaskId) ||
      (task.status === TaskStatus.CLIENT_APPROVAL && !!task.delegateTaskId);
    if (!awaitingThisOrg) return false;

    if (role !== UserRoles.SUPERINTENDENT || !task?.superintendentId) return true;

    // A superintendent may approve only a task assigned to them. `superintendentId` holds an
    // employee id, so compare it with the current user's employee record on this project.
    return (
      project?.superintendents.find(
        (superintendent: any) => superintendent?.user?.userId === userId,
      )?.employeeId === task.superintendentId
    );
  };

  const updateApprovedTask = async (taskId: string) => {
    setLoading(true);
    try {
      // The server decides what approving means for this task (complete it, send it to the client,
      // or forward it up a delegation chain) and records it in the activity feed.
      await graphQlClient.request(TasksGraphQL.mutations.approveTask, { id: taskId });
      dispatch(
        appendActionMessage({
          message: localeCommon.taskApprovedSuccessfully,
          type: SnackType.SUCCESS,
        }),
      );
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || 'Error approving task',
          type: SnackType.ERROR,
        }),
      );
    } finally {
      reloadTasks();
      setLoading(false);
    }
  };

  const updateRejectedTask = async (reason: string) => {
    setLoading(true);
    try {
      const query = TasksGraphQL.mutations.rejectTask;
      const data: any = await graphQlClient.request(query, {
        id: rejectId,
        note: reason,
      });

      if (data?.rejectTask) {
        dispatch(
          appendActionMessage({
            message: localeCommon.taskRejectedSuccessfully,
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || 'Error rejecting task',
          type: SnackType.ERROR,
        }),
      );
    } finally {
      reloadTasks();
      setRejectModal(false);
      setRejectId(null);
      setLoading(false);
    }
  };

  const handleApprove = async (taskId: string) => {
    const task = tasks.find((t: any) => t.id === taskId);
    if (!task || !canApproveTask(task)) return;
    await updateApprovedTask(taskId);
  };

  const handleReject = (taskId: string) => {
    setRejectId(taskId);
    setRejectModal(true);
  };

  const handleTaskClick = (task: any) => {
    if (!task.isEvent) {
      // Map the task data to match the Task model interface
      const mappedTask = {
        ...task,
        taskId: task.id, // Map id to taskId for TaskDetailModal compatibility
      };
      setSelectedTask(mappedTask);
      setIsTaskDetailModalOpen(true);
    }
  };

  const handleEdit = (task: any) => {
    if (task.isEvent) {
      handleEventEdit(task.id);
    } else {
      handleTaskEdit(task.id);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-6">
      <TaskTable
        tasks={tasks}
        taskBudgetRequests={taskBudgetRequests}
        formatDate={formatDate}
        canApprove={canApproveTask}
        onApprove={handleApprove}
        onReject={handleReject}
        onEdit={handleEdit}
        onTaskClick={handleTaskClick}
        onDateClick={onDateClick}
      />

      <RejectionModal
        isOpen={rejectModal}
        onClose={() => {
          setRejectModal(false);
          setRejectId(null);
        }}
        onConfirm={updateRejectedTask}
        loading={loading}
      />

      <TaskDetailModal
        project={project}
        task={selectedTask}
        isTaskDetailModal={isTaskDetailModalOpen}
        setIsTaskDetailModal={setIsTaskDetailModalOpen}
      />
    </div>
  );
}
